import type { FC, FormEvent } from "react";
import { useEffect, useState } from "react";
import { FormControl, FormHelperText, TextField } from "@mui/material";
import { EmailRegex } from "@utils/regex";
import {
  LoginInfoIcon,
  LoginMailIcon,
  YggLogo,
} from "@features/common/SvgIcons";
import Button from "@features/common/button/Button";
import HelpText from "../helpText/HelpText";
import FbWarning from "../fbWarning/FbWarning";
import { useVerifyEmailMutation } from "../authApi";
import styles from "./LoginForm.module.scss";

// ============================================================================
// Types & Interfaces
// ============================================================================

interface LoginFormProps {
  onEmailVerified: (email: string) => void;
  initialEmail?: string;
  onEmailChange?: React.Dispatch<React.SetStateAction<string>>;
}


const LoginForm: FC<LoginFormProps> = ({
  onEmailVerified,
  initialEmail = "",
  onEmailChange,
}) => {
  // ============================================================================
  // State Management
  // ============================================================================
  const [email, setEmail] = useState(initialEmail);
  const [emailHelperText, setEmailHelperText] = useState("");
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [showFbWarning, setShowFbWarning] = useState<boolean>(false);

  // ============================================================================
  // API Hooks
  // ============================================================================
  const [
    verifyEmail,
    {
      data: verifyEmailData,
      error: verifyEmailError,
      isSuccess: verifyEmailSuccess,
      isLoading: verifyEmailLoading,
    },
  ] = useVerifyEmailMutation();

  // ============================================================================
  // Helper Functions
  // ============================================================================

  const clearFormErrors = () => {
    setEmailHelperText("");
  };

  const validateForm = () => {
    let isValid = false;
    if (!EmailRegex.test(email)) {
      setEmailHelperText("Invalid Email");
    } else {
      isValid = true;
    }
    return isValid;
  };

  const handleFBLogin = () => {
    setShowFbWarning(true);
  };

  // ============================================================================
  // Event Handlers
  // ============================================================================

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    onEmailChange?.(e.target.value);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearFormErrors();

    if (validateForm()) {
      try {
        verifyEmail({
          username: email,
          is_encrypted: false,
        });
      } catch (err) {
        console.error("Email verification failed:", err);
      }
    }
  };

  // ============================================================================
  // Effects
  // ============================================================================

  /**
   * Controls submit button disabled state based on email input
   * Button is disabled when email field is empty
   */
  useEffect(() => {
    setIsSubmitDisabled(!email);
  }, [email]);

  /**
   * Syncs local email state with parent-provided initialEmail
   * Handles case when user clicks "Edit" on password screen
   */
  useEffect(() => {
    if (initialEmail && initialEmail !== email) {
      setEmail(initialEmail);
    }
  }, [initialEmail]);

  /**
   * Handles email verification API response
   * On success: triggers parent callback to proceed to password screen
   * On error: displays error message to user
   */
  useEffect(() => {
    if (verifyEmailSuccess) {
      const verifiedEmail = verifyEmailData?.data?.username;
      if (verifiedEmail) {
        onEmailVerified(verifiedEmail);
      }
    }

    if (verifyEmailError) {
      const errorMessage =
        (verifyEmailError as any)?.data?.message ||
        "Email verification failed. Please try again.";
      setEmailHelperText(errorMessage);
    }
  }, [verifyEmailData, verifyEmailError, verifyEmailSuccess, onEmailVerified]);

  return (
    <div className={styles["signin-email"]}>
      {/* Header Section */}
      <div className={styles["header"]}>
        <YggLogo />
        <h4>Enter your business email</h4>
      </div>

      {/* Email Input Form */}
      <form className="email-login" onSubmit={handleSubmit}>
        <div
          className={`${styles["email-input-button"]} ${
            emailHelperText ? styles["error-outline"] : ""
          }`}
        >
          {/* Email Input Field */}
          <div className={styles["email-input-button__input"]}>
            <LoginMailIcon />
            <FormControl variant="standard" fullWidth>
              <TextField
                onChange={handleChange}
                value={email}
                autoFocus
                name="email"
                id="email"
                type="text"
                variant="outlined"
                placeholder="mail@workemail.com"
                inputProps={{ maxLength: 150 }}
                sx={{ borderBottom: "none" }}
                onFocus={clearFormErrors}
              />
            </FormControl>
          </div>

          {/* Submit Button */}
          <div
            className={`button-container-email ${styles["button-container-email"]}`}
          >
            <Button
              id="btnSubmit"
              theme="purple"
              attribues={{
                disabled: isSubmitDisabled || verifyEmailLoading,
                type: "submit",
              }}
              className={`submit-button ${styles["email-input-button__button"]}`}
            >
              Continue
            </Button>
          </div>
        </div>

        {/* Error Message */}
        {emailHelperText && (
          <FormHelperText
            error
            className={styles["phone-verification__form-input-validation"]}
          >
            {emailHelperText}
          </FormHelperText>
        )}
      </form>

      {/* Help Section */}
      <div className={styles["trouble-signin-container"]}>
        <div className={styles["trouble-signin"]}>
          <LoginInfoIcon />
          <span className={styles["content"]} onClick={handleFBLogin}>
            Trouble signing in?
          </span>
        </div>
        <HelpText />
      </div>
      <FbWarning open={showFbWarning} onClose={setShowFbWarning} />
    </div>
  );
};

export default LoginForm;
